import { useState, useEffect, Fragment } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Edit, MoreHoriz } from "@mui/icons-material";

import { CRMService } from "../../services";
import { ActionButton, DeleteModal } from "../v3";
import { AddEditLeadStatus } from "./AddEditLeadStatus.component";
import {
  CRM_PRO_CREATE_LEAD_STATUS,
  CRM_PRO_DELETE_LEAD_STATUS,
  CRM_PRO_EDIT_LEAD_STATUS,
  CRM_PRO_VIEW_ALL_LEAD,
} from "../../constants";
import { useHasPermission } from "../../utils";

const drawerWidth = 240;

export const CrmSidebar = ({ onStatusClick = () => {}, reloadData, taskLeadCounts = {}, selectedStatusId = null }) => {
  const [statuses, setStatuses] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  // Seeded from the page so a remembered status is highlighted when the user comes back to the list.
  const [selectedStatus, setSelectedStatus] = useState(selectedStatusId === null ? null : { statusId: selectedStatusId });
  const [statusId, setStatusId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    statusId: null,
  });

  const hasCreateLeadStatus = useHasPermission(CRM_PRO_CREATE_LEAD_STATUS);
  const hasEditOrDeleteLeadStatus = useHasPermission([CRM_PRO_EDIT_LEAD_STATUS, CRM_PRO_DELETE_LEAD_STATUS]);
  const hasEditLeadStatus = useHasPermission(CRM_PRO_EDIT_LEAD_STATUS);
  const hasDeleteLeadStatus = useHasPermission(CRM_PRO_DELETE_LEAD_STATUS);

  // Follow the page's status filter when it changes outside the sidebar (restored on Back, reset by the menu link).
  useEffect(() => {
    setSelectedStatus((current) => {
      if ((current?.statusId ?? null) === selectedStatusId) return current;
      return selectedStatusId === null ? null : { statusId: selectedStatusId };
    });
  }, [selectedStatusId]);

  useEffect(() => {
    fetchData();
  }, [reloadData]);

  const fetchData = async () => {
    try {
      const { data } = await CRMService.getLeadStatuses({ page: 1, limit: 100 });
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setStatuses(rows.map((s) => ({ ...s, statusId: s.id, statusName: s.name })));
    } catch (error) {
      console.log("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleStatusClick = (statusDetails) => {
    if (onStatusClick) {
      onStatusClick(statusDetails);
    }
  };

  const handleStatusActionClick = (event, status) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(status);
    setStatusId(status.statusId);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setAnchorEl(null);
    setOpenModal(true);
    setStatusId(selectedStatus.statusId);
  };

  const handleModalClose = () => setOpenModal(false);

  const openDeleteModal = (statusId) => {
    setAnchorEl(null);
    setDeleteModal({ isOpen: true, statusId: statusId });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, statusId: null });
  };

  const handleDeleteStatus = async () => {
    try {
      await CRMService.deleteLeadStatus(deleteModal.statusId);
      toast("Lead status deleted successfully", {
        type: "success",
      });
      fetchData();
    } catch (error) {
      console.log("🚀 ~ handleDeleteStatus ~ error:", error);
      toast(error?.response?.data?.message, { type: "error" });
    } finally {
      closeDeleteModal();
    }
  };

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            position: "relative",
            height: "calc(100vh - 75px - 21px)",
            zIndex: 1000,
            borderRight: "none",
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Divider />
        {hasCreateLeadStatus && (
          <>
            <ActionButton
              buttonText="Add Status"
              buttonIcon={Add}
              onButtonClick={() => {
                setStatusId(null);
                setOpenModal(true);
              }}
              variant="text"
              sx={{
                textTransform: "capitalize",
                fontWeight: 500,
                fontSize: "0.875rem",
                position: "sticky",
                zIndex: 1100,
              }}
            />
            <Divider />
          </>
        )}
        <List sx={{ flex: 1, overflowY: "auto" }}>
          {statuses?.map((status, index) => (
            <Fragment key={status.statusId}>
              <ListItem
                key={status.statusId}
                disablePadding
                onClick={() => {
                  // Toggle: clicking the active status again clears the filter (show all leads).
                  const next = selectedStatus?.statusId === status.statusId ? null : status;
                  setSelectedStatus(next);
                  handleStatusClick(next);
                }}
              >
                <ListItemButton
                  selected={selectedStatus?.statusId === status.statusId}
                  sx={{
                    backgroundColor: selectedStatus?.statusId === status.statusId ? "#F2ECF9" : "inherit",
                    "&.Mui-selected": {
                      backgroundColor: "#F2ECF9",
                      "&:hover": {
                        backgroundColor: "#F2ECF9",
                      },
                    },
                  }}
                >
                  <Tooltip title={status.statusName} placement="top" arrow>
                    <ListItemText
                      primary={status.statusName}
                      slotProps={{
                        primary: {
                          variant: "body2",
                          sx: {
                            textTransform: "capitalize",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                    />
                  </Tooltip>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {taskLeadCounts[status.statusId] ?? 0}
                    </Typography>
                  </Box>
                  {hasEditOrDeleteLeadStatus && (
                    <MoreHoriz
                      onClick={(event) => {
                        event.stopPropagation();
                        handleStatusActionClick(event, status);
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
              <Divider />
            </Fragment>
          ))}
        </List>
      </Drawer>

      {hasEditOrDeleteLeadStatus && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {hasEditLeadStatus && (
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {hasDeleteLeadStatus && (
            <MenuItem onClick={() => openDeleteModal(selectedStatus?.statusId)}>
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Add Edit Lead Status */}
      <AddEditLeadStatus statusId={statusId} open={openModal} onClose={handleModalClose} onSuccess={fetchData} />

      {/* delete status modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteStatus}
        message="Are you sure want to delete this status?"
      />
    </>
  );
};
